/**
 * Asset Service — Frontend API client for unified asset management
 *
 * Handles file uploads, retrieval, deletion, and reordering via AssetService API.
 * Uses multipart/form-data for uploads, JSON for mutations.
 *
 * MVP 1.27.7-S (SCRUM-783) — Experience Creation Wizard Photo Upload
 */

import apiRequest from './api';

/**
 * Upload a single asset file.
 *
 * @param {File} file - File object from input or drop event
 * @param {Object} params - Upload parameters
 * @param {string} params.entityType - Entity type (experience, guide, credential, etc.)
 * @param {string} params.entityId - Entity UUID
 * @param {string} params.category - Asset category (image, document, system_generated)
 * @param {string} params.assetPurpose - Purpose (cover, gallery, profile, etc.)
 * @param {string} [params.caption] - Optional caption
 * @param {string} [params.altText] - Optional alt text
 * @returns {Promise<Object>} Stored asset with variants and CDN URLs
 */
export async function uploadAsset(file, params) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('entityType', params.entityType);
  formData.append('entityId', params.entityId);
  formData.append('category', params.category);
  formData.append('assetPurpose', params.assetPurpose);

  if (params.caption) {
    formData.append('caption', params.caption);
  }
  if (params.altText) {
    formData.append('altText', params.altText);
  }

  // Empty headers so browser sets Content-Type with multipart boundary
  const response = await apiRequest('/api/v1/assets/upload', {
    method: 'POST',
    body: formData,
    headers: {},
  });

  return response.data;
}

/**
 * Upload multiple asset files in batch.
 *
 * @param {File[]} files - Array of File objects
 * @param {Object} params - Upload parameters (same entityType/entityId/category/assetPurpose for all)
 * @param {string} params.entityType
 * @param {string} params.entityId
 * @param {string} params.category
 * @param {string} params.assetPurpose
 * @param {string[]} [params.captions] - Optional per-file captions
 * @param {string[]} [params.altTexts] - Optional per-file alt texts
 * @returns {Promise<Array>} Array of results (success with asset or error per file)
 */
export async function uploadAssetBatch(files, params) {
  const formData = new FormData();

  for (const file of files) {
    formData.append('files', file);
  }

  formData.append('entityType', params.entityType);
  formData.append('entityId', params.entityId);
  formData.append('category', params.category);
  formData.append('assetPurpose', params.assetPurpose);

  if (params.captions) {
    formData.append('captions', JSON.stringify(params.captions));
  }
  if (params.altTexts) {
    formData.append('altTexts', JSON.stringify(params.altTexts));
  }

  const response = await apiRequest('/api/v1/assets/upload/batch', {
    method: 'POST',
    body: formData,
    headers: {},
  });

  return response.data;
}

/**
 * Get assets for a specific entity.
 *
 * @param {string} entityType - Entity type (experience, guide, etc.)
 * @param {string} entityId - Entity UUID
 * @returns {Promise<Array>} Array of assets with variants
 */
export async function getAssetsByEntity(entityType, entityId) {
  const response = await apiRequest(`/api/v1/assets/entity/${entityType}/${entityId}`, {
    method: 'GET',
  });
  return response.data;
}

/**
 * Delete a single asset and its S3 objects.
 *
 * @param {string} assetId - Asset UUID to delete
 * @returns {Promise<Object>} Deletion result
 */
export async function deleteAsset(assetId) {
  const response = await apiRequest(`/api/v1/assets/${assetId}`, {
    method: 'DELETE',
  });
  return response.data;
}

/**
 * Reorder assets for an entity.
 *
 * @param {string} entityType - Entity type
 * @param {string} entityId - Entity UUID
 * @param {string[]} assetIds - Ordered array of asset IDs
 * @returns {Promise<Object>} Reorder result
 */
export async function reorderAssets(entityType, entityId, assetIds) {
  const response = await apiRequest(`/api/v1/assets/entity/${entityType}/${entityId}/reorder`, {
    method: 'PUT',
    body: JSON.stringify({ assetIds }),
  });
  return response.data;
}

/**
 * Set a specific asset as the primary (cover) for an entity.
 *
 * @param {string} entityType - Entity type
 * @param {string} entityId - Entity UUID
 * @param {string} assetId - Asset UUID to set as primary
 * @returns {Promise<Object>} Updated asset
 */
export async function setPrimaryAsset(entityType, entityId, assetId) {
  const response = await apiRequest(`/api/v1/assets/entity/${entityType}/${entityId}/primary/${assetId}`, {
    method: 'PUT',
  });
  return response.data;
}

/**
 * Get the best CDN URL for display from an asset's variants.
 * Picks the specified size, falling back to medium → small → thumbnail → original.
 *
 * @param {Object} asset - Asset object with variants array
 * @param {string} [preferredSize='medium'] - Preferred variant size
 * @returns {string|null} CDN URL or null if no variants
 */
export function getAssetUrl(asset, preferredSize = 'medium') {
  if (!asset || !asset.variants || asset.variants.length === 0) {
    return null;
  }

  const sizePreference = [preferredSize, 'medium', 'small', 'thumbnail', 'original'];

  for (const size of sizePreference) {
    const variant = asset.variants.find(v => v.size_name === size);
    if (variant) {
      return variant.cdn_url;
    }
  }

  // Fallback: first variant
  return asset.variants[0].cdn_url;
}

/**
 * Build an HTML srcSet string from an asset's variants.
 * Returns width descriptors (e.g. "url 150w, url 400w, ...") suitable for <img srcSet>.
 *
 * MVP 1.27.7-S (SCRUM-793) — Responsive Image Support
 *
 * @param {Object} asset - Asset object with variants array (or flat variants array)
 * @returns {string|null} srcSet string or null if no variants with width
 */
export function buildSrcSet(asset) {
  const variants = Array.isArray(asset) ? asset : asset?.variants;
  if (!variants || variants.length === 0) {
    return null;
  }

  const entries = variants
    .filter(v => v.cdn_url && v.width)
    .map(v => `${v.cdn_url} ${v.width}w`);

  return entries.length > 0 ? entries.join(', ') : null;
}

/**
 * Get a recommended sizes attribute for responsive images based on display context.
 *
 * @param {'card'|'hero'|'thumbnail'|'profile'|'full'} context - Display context
 * @returns {string} HTML sizes attribute value
 */
export function getResponsiveSizes(context) {
  switch (context) {
    case 'thumbnail':
      return '80px';
    case 'profile':
      return '128px';
    case 'card':
      return '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw';
    case 'hero':
      return '(max-width: 640px) 100vw, (max-width: 1280px) 100vw, 1280px';
    case 'full':
      return '100vw';
    default:
      return '(max-width: 640px) 100vw, 50vw';
  }
}

export default {
  uploadAsset,
  uploadAssetBatch,
  getAssetsByEntity,
  deleteAsset,
  reorderAssets,
  setPrimaryAsset,
  getAssetUrl,
  buildSrcSet,
  getResponsiveSizes,
};
